import { useState, useEffect, useCallback } from 'react'
import { getLocalApiUrl } from '../config/api'

const HEALTH_CHECK_INTERVAL = 30000 // 30 seconds
const HEALTH_TIMEOUT = 5000 // 5 seconds

export function useSystemHealth() {
  const [status, setStatus] = useState('checking') // 'online' | 'degraded' | 'offline' | 'checking'
  const [lastCheck, setLastCheck] = useState(null)
  const [responseTime, setResponseTime] = useState(null)

  const checkHealth = useCallback(async () => {
    const startTime = Date.now()
    
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), HEALTH_TIMEOUT)
      
      const response = await fetch(getLocalApiUrl('/health'), {
        signal: controller.signal,
      })
      
      clearTimeout(timeoutId)
      
      const elapsed = Date.now() - startTime
      setResponseTime(elapsed)
      setLastCheck(new Date())
      
      if (response.ok) {
        const data = await response.json()
        // Check if databases are healthy
        const dbHealthy = data.databases?.main?.status === 'healthy'
        if (dbHealthy && elapsed < 2000) {
          setStatus('online')
        } else if (dbHealthy) {
          setStatus('degraded') // Slow but working
        } else {
          setStatus('degraded')
        }
      } else {
        setStatus('degraded')
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        setStatus('degraded') // Timeout
      } else {
        setStatus('offline')
      }
      setLastCheck(new Date())
    }
  }, [])

  useEffect(() => {
    // Initial check
    checkHealth()
    
    // Periodic checks
    const interval = setInterval(checkHealth, HEALTH_CHECK_INTERVAL)
    
    return () => clearInterval(interval)
  }, [checkHealth])

  return { status, lastCheck, responseTime, refresh: checkHealth }
}

export default useSystemHealth
